import { CompanySearchRequestProperty, FilterOperator, IFilterCriteria } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import equal from 'fast-deep-equal';
import produce from 'immer';
import * as React from 'react';
import { Button } from '../../../../components/Button';
import { AssigneeIcon } from '../../../../components/svgs/icons/AssigneeIcon';
import { SearchIcon } from '../../../../components/svgs/icons/SearchIcon';
import { TagIcon } from '../../../../components/svgs/icons/TagIcon';
import { brandPrimary, header } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import {
	CompaniesTab,
	DefaultCompaniesFilter,
	DefaultCompaniesSort,
	getCompanyCriteriaFromTab,
	getCompanySearchFromCriteria,
	getCompanyTabFromCriteria,
	useCompaniesInput,
	useCompaniesSearch,
} from '../CompaniesContext';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const CompaniesSearchBarTypeTabs: React.FC<IProps> = props => {
	const { className, styles } = props;
	const { filter, setFilter, sort, setSort } = useCompaniesSearch();
	const { search } = useCompaniesInput();

	const onTabClick = React.useCallback(
		(newTab: CompaniesTab) => () => {
			const nextFilter = produce(filter, draftFilter => {
				const newCriteria = draftFilter.criteria.filter(x => {
					return (
						x.property !== CompanySearchRequestProperty.All &&
						x.property !== CompanySearchRequestProperty.Name &&
						x.property !== CompanySearchRequestProperty.Tag
					);
				});

				const criterion: IFilterCriteria<CompanySearchRequestProperty, FilterOperator> = {
					property: getCompanyCriteriaFromTab(newTab),
				};
				if (search) {
					criterion.value = search;
				}

				newCriteria.push(criterion);
				draftFilter.criteria = newCriteria;
			});

			setFilter(nextFilter);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[filter, sort]
	);

	const onClear = () => {
		setFilter(DefaultCompaniesFilter);
		setSort(DefaultCompaniesSort);
	};

	const searched = React.useMemo(() => {
		if (!filter?.criteria) return false;
		return getCompanySearchFromCriteria(filter.criteria);
	}, [filter]);

	const hasSearched = !equal(DefaultCompaniesFilter, filter);

	const tab = getCompanyTabFromCriteria(filter.criteria);

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<div className={css(styleSheet.tabContainer, !searched && baseStyleSheet.hidden)}>
				<FakeTab onClick={onTabClick} tab='All' selectedTab={tab}>
					<SearchIcon className={css(styleSheet.tabIcon)} fillColor={tab === 'All' ? brandPrimary : header} />
				</FakeTab>
				<FakeTab onClick={onTabClick} tab='Name' selectedTab={tab}>
					<AssigneeIcon
						className={css(styleSheet.tabIcon)}
						style={{ color: tab === 'Name' ? brandPrimary : header }}
						strokeWidth='1.5'
					/>
				</FakeTab>
				<FakeTab onClick={onTabClick} tab='Tag' selectedTab={tab}>
					<TagIcon className={css(styleSheet.tabIcon)} fillColor={tab === 'Tag' ? brandPrimary : header} />
				</FakeTab>
			</div>
			{hasSearched && (
				<Button kind='reverse' onClick={onClear}>
					Clear search
				</Button>
			)}
		</div>
	);
};

interface ITabProps {
	onClick(newTab: CompaniesTab): () => void;
	tab: CompaniesTab;
	selectedTab: CompaniesTab;
}

const FakeTab: React.FC<React.PropsWithChildren<ITabProps>> = ({ onClick, children, tab, selectedTab }) => {
	return (
		<div className={css(styleSheet.tab, tab === selectedTab && styleSheet.tabSelected)} onClick={onClick(tab)}>
			{children}
			<span className={css(styleSheet.tabText, tab === selectedTab && styleSheet.tabTextSelected)}>{tab}</span>
		</div>
	);
};
